<template>
  <div class="col-lg-12 col-md-12 box-col-12">
    <div class="card best-seller">
      <div class="card-header pb-0">
        <div class="d-flex justify-content-between align-items-center">
          <div class="flex-grow-1">
            <p class="square-after f-w-600">Últimos Pedidos<i class="fa fa-circle"> </i></p>
          </div>
          <div>
            <router-link to="/pedidos" class="btn btn-outline-primary">
              Ver Todos
              <i class="icofont icofont-social-prestashop"></i>
            </router-link>
          </div>
        </div>
      </div>
      <div class="card-body pt-0">
        <div class="table-responsive theme-scrollbar">
          <table class="table table-bordernone">
            <thead>
              <tr>
                <th>Sucursal</th>
                <th>Cliente</th>
                <th>Fecha</th>
                <th>Total</th>
                <th>Tipo de Entrega</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody v-if="loading">
              <tr>
                <td colspan="7" class="text-center text-muted py-4">
                  <div class="spinner-border text-primary" role="status">
                    <span class="sr-only">Cargando...</span>
                  </div>
                </td>
              </tr>
            </tbody>
            <tbody v-else-if="!pedidos || pedidos.length === 0">
              <tr>
                <td colspan="7" class="text-center text-muted py-4">No hay pedidos disponibles</td>
              </tr>
            </tbody>
            <tbody v-else>
              <tr v-for="pedido in pedidos" :key="pedido.pedidoKey">
                <td>{{ getStoreName(pedido.division) }}</td>
                <td>{{ pedido.cliente || 'N/A' }}</td>
                <td>{{ formatDate(pedido.timeSolicitud) }}</td>
                <td>${{ formatNumber(pedido.total) }}</td>
                <td>
                  <span
                    class="badge py-2 px-2"
                    :class="pedido.tipoEntrega === 'pickup' ? 'badge-primary' : 'badge-info'"
                  >
                    {{ pedido.tipoEntrega === 'pickup' ? 'Pickup' : 'Delivery' }}
                  </span>
                </td>
                <td>
                  <span class="badge py-2 px-2" :class="getStatusBadgeClass(pedido.status)">
                    {{ getStatusLabel(pedido.status) }}
                  </span>
                </td>
                <td>
                  <button
                    type="button"
                    class="btn btn-sm btn-primary"
                    @click="viewDetail(pedido.pedidoKey)"
                    title="Ver Detalle"
                  >
                    <vue-feather type="eye" size="14"></vue-feather>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getLastPedidos } from '@/services/pedidosService'
import { getAllStores } from '@/services/storesService'

export default {
  name: 'BestSeller',
  data() {
    return {
      pedidos: [],
      stores: [],
      loading: false,
    }
  },
  async created() {
    await this.loadStores()
    await this.loadPedidos()
  },
  methods: {
    /**
     * Load stores for mapping division to store name
     */
    async loadStores() {
      try {
        const result = await getAllStores()
        if (result.success) {
          this.stores = result.data || []
        } else {
          console.error('Error loading stores:', result.error)
          this.stores = []
        }
      } catch (error) {
        console.error('Error loading stores:', error)
        this.stores = []
      }
    },
    /**
     * Load last 10 pedidos with 10-minute cache
     */
    async loadPedidos() {
      const CACHE_KEY = 'bestSeller_pedidos'
      const CACHE_TIMESTAMP_KEY = 'bestSeller_pedidos_timestamp'
      const CACHE_DURATION = 10 * 60 * 1000 // 10 minutes in milliseconds

      // Check cache first
      try {
        const cachedData = localStorage.getItem(CACHE_KEY)
        const cachedTimestamp = localStorage.getItem(CACHE_TIMESTAMP_KEY)

        if (cachedData && cachedTimestamp) {
          const now = Date.now()
          const cacheAge = now - parseInt(cachedTimestamp, 10)

          if (cacheAge < CACHE_DURATION) {
            // Cache is still valid
            this.pedidos = JSON.parse(cachedData)
            return
          } else {
            // Cache expired, remove it
            localStorage.removeItem(CACHE_KEY)
            localStorage.removeItem(CACHE_TIMESTAMP_KEY)
          }
        }
      } catch (error) {
        console.error('Error reading cache:', error)
        // Continue to fetch fresh data
      }

      // Fetch fresh data
      this.loading = true
      try {
        const result = await getLastPedidos(10)
        if (result.success) {
          this.pedidos = result.data || []

          // Save to cache
          try {
            localStorage.setItem(CACHE_KEY, JSON.stringify(this.pedidos))
            localStorage.setItem(CACHE_TIMESTAMP_KEY, Date.now().toString())
          } catch (error) {
            console.error('Error saving to cache:', error)
            // Non-critical error, continue
          }
        } else {
          console.error('Error loading pedidos:', result.error)
          this.pedidos = []
        }
      } catch (error) {
        console.error('Error loading pedidos:', error)
        this.pedidos = []
      } finally {
        this.loading = false
      }
    },
    /**
     * Get store name by division number
     */
    getStoreName(division) {
      if (!division || division === '0' || division === 0) return 'N/A'

      const store = this.stores.find(
        s =>
          s.division === division ||
          s.division === String(division) ||
          String(s.division) === String(division)
      )

      return store ? store.sucursal : division
    },
    /**
     * Format number to currency
     */
    formatNumber(value) {
      if (value === null || value === undefined) return '0.00'
      const num = Number(value)
      if (isNaN(num)) return '0.00'
      return new Intl.NumberFormat('es-MX', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(num)
    },
    /**
     * Format timestamp to date string
     */
    formatDate(timestamp) {
      if (!timestamp) return 'N/A'
      const date = new Date(timestamp)
      return new Intl.DateTimeFormat('es-MX', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      }).format(date)
    },
    /**
     * Get status label in titleCase
     */
    getStatusLabel(status) {
      if (!status) return 'N/A'
      return status
        .toLowerCase()
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    },
    /**
     * Get status badge class with colors
     */
    getStatusBadgeClass(status) {
      if (!status) return 'badge-status-default'

      const statusLower = status.toLowerCase()

      if (statusLower.includes('solicitud') || statusLower === 'solicitado') {
        return 'badge-status-solicitud'
      } else if (statusLower.includes('proceso') || statusLower.includes('procesando')) {
        return 'badge-status-proceso'
      } else if (statusLower.includes('ruta')) {
        return 'badge-status-ruta'
      } else if (
        statusLower.includes('concluido') ||
        statusLower.includes('completado') ||
        statusLower.includes('entregado')
      ) {
        return 'badge-status-concluido'
      } else if (statusLower.includes('cancelado') || statusLower.includes('cancel')) {
        return 'badge-status-cancelado'
      }

      return 'badge-status-default'
    },
    /**
     * Navigate to pedido detail page
     */
    viewDetail(pedidoKey) {
      if (pedidoKey) {
        this.$router.push(`/pedidos/${pedidoKey}`)
      }
    },
  },
}
</script>
